import React, { useEffect, useState } from 'react';
import { wfhAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { HomeIcon, Plus, Loader2, X } from 'lucide-react';

const STATUS_VARIANT = {
  pending_manager: 'warning', manager_approved: 'default', approved: 'success', rejected: 'destructive', cancelled: 'secondary',
};

const MyWFH = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ start_date: today, end_date: today, reason: '', location: '' });

  const fetchData = async () => {
    setLoading(true);
    try { const r = await wfhAPI.mine(); setRows(r.data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const submit = async () => {
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      await wfhAPI.create(form);
      toast.success('WFH request submitted');
      setOpen(false);
      setForm({ start_date: today, end_date: today, reason: '', location: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await wfhAPI.cancel(id); toast.success('Cancelled'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><HomeIcon size={22} />Work From Home</h1>
          <p className="text-muted-foreground text-sm">Request WFH days and track approvals.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="mywfh-new-btn"><Plus size={14} className="mr-1" />Request WFH</Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No WFH requests yet.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="mywfh-list">
          {rows.map((w) => (
            <Card key={w.id} data-testid={`mywfh-row-${w.id}`}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                <HomeIcon size={16} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{w.start_date} → {w.end_date}</span>
                    <Badge variant={STATUS_VARIANT[w.status] || 'secondary'}>{w.status.replace('_', ' ')}</Badge>
                    <span className="text-xs text-muted-foreground">{w.days} day(s)</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{w.reason} {w.location ? `· ${w.location}` : ''}</div>
                  {w.rejection_note && <div className="text-xs text-red-600 mt-1">Rejected: {w.rejection_note}</div>}
                </div>
                {['pending_manager', 'manager_approved'].includes(w.status) && (
                  <Button size="sm" variant="ghost" onClick={() => cancel(w.id)} data-testid={`mywfh-cancel-${w.id}`}><X size={14} className="text-red-600" /></Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="mywfh-dialog">
          <DialogHeader><DialogTitle>Request WFH</DialogTitle><DialogDescription>Send a request to your manager. HR will approve after your manager.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="mywfh-start" /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="mywfh-end" /></div>
            <div className="col-span-2"><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for working from home" data-testid="mywfh-reason" /></div>
            <div className="col-span-2"><Label>Location (optional)</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City / home address" data-testid="mywfh-location" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="mywfh-submit">{busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyWFH;
